import { z } from "zod";
import type { SupportedCountryCode } from "../constants/countries";

// This RegEx is to prevent characters unsupported by Doordash on the name fields
// This validation also happens on the backend, this is just to catch the issue earlier for the user
const illegalNameCharacters = /^[^(`@#$%^&*+=?;[{\]})]*$/;

export type UserData = {
  firstName: string;
  lastName: string;
  email: string;
};

const processIssues = (issues: z.ZodIssue[]) => {
  return issues.reduce((acc: Record<string, string>, issue) => {
    const {
      path: [key],
      message
    } = issue;
    return acc[key] ? acc : { ...acc, [key]: message };
  }, {});
};

export type UserDetailsFieldNames = "first_name" | "last_name" | "email";

const getFieldValidators = (copy: Record<string, string>) => {
  return {
    firstName: z
      .string()
      .trim()
      .min(1, { message: copy.firstNameEmpty })
      .min(2, { message: copy.firstNameInvalid })
      .regex(illegalNameCharacters, { message: copy.firstNameContainsIllegalCharacters }),
    lastName: z
      .string()
      .trim()
      .min(1, { message: copy.lastNameEmpty })
      .min(2, { message: copy.lastNameInvalid })
      .regex(illegalNameCharacters, { message: copy.lastNameContainsIllegalCharacters }),
    email: z
      .string()
      .trim()
      .min(1, { message: copy.emailEmpty })
      .email(copy.emailInvalid)
      .transform(value => value.toLowerCase())
  };
};

export const FieldsValidationHelper = {
  validateAllFields: (
    userData: UserData,
    copy: Record<string, string>
  ) => {
    const UserDataSchema = z.object(getFieldValidators(copy));
    const { success, data, error } = UserDataSchema.safeParse(userData);
    return { success, data, issues: error ? processIssues(error.issues) : {} };
  }
};

export const limitNumberFields = (field: number | string, limit: number): string => {
  if (Number(field) > limit) {
    return String(limit);
  }

  while (String(field ?? '').startsWith('0')) {
    return String(field).slice(1);
  }

  if (Number(field) < 0) {
    return ''
  }

  return String(field);
};
